import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Loader2, Check, SlidersHorizontal, Send, CreditCard } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { PageTitle } from "@/components/site/ReleaseCard";
import { BeforeAfter } from "@/components/site/BeforeAfter";

const STRIPE_LINK = "https://buy.stripe.com/aFa5kF2tqefu5zj6n8asg00";

const BuyBanner = () => (
  <div className="max-w-7xl mx-auto px-5 sm:px-8 mb-8">
    <div className="rounded-3xl border border-blue-500/40 bg-blue-600/10 p-6 sm:p-8 flex flex-col md:flex-row md:items-center gap-5 md:justify-between" data-testid="services-stripe-banner">
      <div>
        <p className="font-display text-xl font-bold uppercase">Mastering e caricamento brani</p>
        <p className="text-sm text-zinc-300 mt-1">Acquista il servizio in modo sicuro con Stripe: carta, Apple Pay e Google Pay.</p>
      </div>
      <a href={STRIPE_LINK} target="_blank" rel="noopener noreferrer" data-testid="services-stripe-buy-btn"
        className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white px-6 sm:px-8 py-4 font-bold text-center shadow-[0_0_30px_rgba(37,99,235,0.45)] transition-[background-color,transform] hover:-translate-y-0.5 md:whitespace-nowrap">
        <CreditCard className="h-5 w-5" /> Acquista mastering e caricamento brani
      </a>
    </div>
  </div>
);

const ICONS = { mastering: SlidersHorizontal, submission: Send };
const cls = "w-full rounded-xl bg-[#262b42] border border-white/15 px-4 py-3 text-sm outline-none focus:border-emerald-400";
export const euro = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v || 0);

const OrderForm = ({ service, cfg }) => {
  const [f, setF] = useState({ name: "", email: "", track_title: "", notes: "", accept_terms: false, accept_withdrawal: false });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const upload = async (e) => {
    const fl = e.target.files?.[0];
    if (!fl) return;
    setBusy("Caricamento del brano…");
    const fd = new FormData(); fd.append("file", fl);
    try { const { data } = await api.post("/orders/upload", fd); setFile(data); toast.success("Brano caricato"); }
    catch (err) { toast.error(formatError(err)); } finally { setBusy(""); e.target.value = ""; }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Carica prima il tuo brano");
    setBusy("Apertura del pagamento sicuro…");
    try {
      const { data } = await api.post("/orders", { ...f, service, file_path: file.file_path, origin_url: window.location.origin });
      localStorage.setItem("tga_last_order", JSON.stringify({ id: data.order_id, token: data.token }));
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatError(err)); setBusy(""); }
  };
  return (
    <form onSubmit={submit} className="space-y-3 mt-6" data-testid={`order-form-${service}`}>
      <div className="grid sm:grid-cols-2 gap-3">
        <input required maxLength={120} placeholder="Nome / Nome artista" value={f.name} onChange={set("name")} className={cls} data-testid={`order-${service}-name`} />
        <input required type="email" placeholder="Email" value={f.email} onChange={set("email")} className={cls} data-testid={`order-${service}-email`} />
      </div>
      <input required maxLength={200} placeholder="Titolo del brano" value={f.track_title} onChange={set("track_title")} className={cls} data-testid={`order-${service}-title`} />
      <textarea maxLength={3000} rows={3} placeholder={service === "mastering" ? "Note: stile, riferimenti, volume desiderato…" : "Presentati e racconta il brano…"} value={f.notes} onChange={set("notes")} className={cls} data-testid={`order-${service}-notes`} />
      {service === "mastering" && <p className="text-xs text-emerald-300/90" data-testid="order-mastering-studio-note">Dopo il pagamento puoi masterizzare il brano tu stesso nel nostro studio online (preset per genere, brano di riferimento, export WAV/MP3) oppure lasciarlo fare a noi.</p>}
      <label className="flex items-center gap-3 rounded-xl border border-dashed border-white/25 p-4 cursor-pointer hover:border-emerald-400" data-testid={`order-${service}-upload`}>
        {file ? <Check className="h-5 w-5 text-emerald-400" /> : <Upload className="h-5 w-5" />}
        <span className="text-sm">{file ? file.filename : "Carica il brano (WAV, MP3, FLAC — max 150MB)"}</span>
        <input type="file" accept="audio/*" hidden onChange={upload} data-testid={`order-${service}-file-input`} />
      </label>
      <label className="flex gap-2 text-xs text-zinc-300"><input type="checkbox" required checked={f.accept_terms} onChange={set("accept_terms")} data-testid={`order-${service}-terms`} /> Ho letto e accetto i <Link to="/termini" className="text-emerald-400 underline" target="_blank">Termini e Condizioni</Link> e l'informativa privacy.</label>
      <label className="flex gap-2 text-xs text-zinc-300"><input type="checkbox" required checked={f.accept_withdrawal} onChange={set("accept_withdrawal")} data-testid={`order-${service}-withdrawal`} /> Chiedo che il servizio inizi subito e prendo atto che, una volta eseguito completamente, perdo il diritto di recesso (art. 59 Codice del Consumo).</label>
      <button disabled={!!busy} className="w-full rounded-full bg-emerald-500 text-[#151827] py-3.5 font-bold hover:bg-emerald-400 disabled:opacity-60 inline-flex justify-center items-center gap-2" data-testid={`order-${service}-pay-btn`}>
        {busy ? <><Loader2 className="h-4 w-4 animate-spin" />{busy}</> : `Paga ${euro(cfg.price)} e invia`}
      </button>
    </form>
  );
};

export default function Services() {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/services").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  if (!cfg) return <div className="pt-40 text-center text-zinc-500">Caricamento…</div>;
  const { legal } = cfg;
  return (
    <div data-testid="services-page">
      <PageTitle over="Servizi" title="Lavora con noi" sub="Mastering professionale e invio dei tuoi brani all'etichetta. Pagamento sicuro con carta tramite Stripe." />
      <BuyBanner />
      <BeforeAfter example={cfg.example} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-6">
        {Object.entries(cfg.services).map(([k, s]) => {
          const Icon = ICONS[k] || Send;
          return (
            <div key={k} className="lift rounded-3xl border border-white/10 bg-[#1e2236] p-6 sm:p-8" data-testid={`service-card-${k}`}>
              <div className="flex items-start justify-between gap-4">
                <span className="h-12 w-12 rounded-2xl bg-emerald-400/15 text-emerald-300 grid place-items-center"><Icon className="h-6 w-6" /></span>
                <div className="text-right">
                  <p className="font-display text-3xl font-black" data-testid={`service-price-${k}`}>{s.enabled ? euro(s.price) : "—"}</p>
                  <p className="text-[11px] text-zinc-400">IVA inclusa · {s.delivery_days ? `consegna in ${s.delivery_days} giorni` : "consegna immediata"}</p>
                </div>
              </div>
              <h2 className="font-display text-2xl font-bold uppercase mt-5">{s.title}</h2>
              <p className="text-zinc-300 mt-2 whitespace-pre-line">{s.description}</p>
              {s.enabled ? (open === k ? <OrderForm service={k} cfg={s} /> : (
                <button onClick={() => setOpen(k)} className="mt-6 rounded-full bg-emerald-500 text-[#151827] px-7 py-3 font-bold hover:bg-emerald-400" data-testid={`service-order-btn-${k}`}>Ordina ora</button>
              )) : <p className="mt-6 text-sm text-amber-400" data-testid={`service-unavailable-${k}`}>Servizio in arrivo.</p>}
            </div>
          );
        })}
      </div>
      <p className="max-w-7xl mx-auto px-5 sm:px-8 mt-10 text-xs text-zinc-400" data-testid="seller-info">
        Venditore: {legal.business_name || "[Ragione sociale]"} · P.IVA {legal.vat_number || "[P.IVA]"} · {legal.address || "[Indirizzo sede]"} · {legal.email || "[Email]"} · <Link to="/termini" className="underline">Termini e Condizioni</Link>
      </p>
    </div>
  );
}
